<template>
  <Teleport to="body" v-if="isVisible">
    <div class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <!-- Cabeçalho -->
        <div class="modal-header">
          <h6 class="mb-0">{{ modalTitle }}</h6>
          <i class="bi bi-x-circle close-icon" @click="close"></i>
        </div>

        <!-- Corpo -->
        <div class="modal-content">
          <div v-if="loading" class="text-center py-3">Carregando…</div>

          <div v-else>
            <div v-if="erro" class="alert alert-danger">{{ erro }}</div>

            <div v-else>
              <div class="mb-2">
                <strong>Para: </strong>
                <span>{{ displayTo }}</span>
              </div>

              <div class="mb-2">
                <strong>Assunto:</strong> {{ modalTitle }}
              </div>

              <div class="mb-3">
                <div class="email-preview border rounded p-3" v-safe-html="emailHtml"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Rodapé -->
        <div class="modal-footer d-flex justify-content-end gap-2">
          <button
            class="btn btn-secondary btn-sm"
            @click="close"
            :disabled="isSending"
          >
            Cancelar
          </button>

          <button
            class="btn btn-primary btn-sm"
            :disabled="readOnly || isSending || !emailTo"
            @click="enviar"
          >
            {{ isSending
              ? 'Enviando…'
              : (modoReenvio ? 'Reenviar e-mail' : 'Enviar e-mail')
            }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'

export default {
  name: 'EmailAvaliacaoModal',

  props: {
    show: { type: Boolean, default: false },
    respostaId: { type: [Number, String], required: false },
    readOnly: { type: Boolean, default: false },
    modoReenvio: { type: Boolean, default: false }
  },

  data () {
    return {
      loading: false,
      erro: null,
      preview: null,
      isSending: false
    }
  },

  computed: {
    isVisible () {
      return !!this.show
    },

    emailTo () {
      return this.preview?.responsavel_email || ''
    },

    displayTo () {
      const nome = this.preview?.responsavel_nome || ''
      const mail = this.preview?.responsavel_email || ''

      if (nome && mail) return `${nome} <${mail}>`
      return mail || nome || '—'
    },

    modalTitle () {
      if (!this.preview) return 'Enviar Avaliação por E-mail'

      const aluno = this.preview.aluno || ''
      const data  = this.fmtDate(this.preview.data_realizacao)
      const aval  = this.preview.avaliacao || ''

      return `${aval} – ${aluno} – ${data}`
    },

    emailHtml () {
      if (!this.preview) return ''

      const itens = (this.preview.resumo || [])
        .map(p => {
          const pergunta = this.escapeHtml(p.pergunta)
          const resposta = this.escapeHtml(this.formatResposta(p.resposta))

          return `<li><strong>${pergunta}:</strong> ${resposta}</li>`
        })
        .join('')

      const lista = `
        <div style="margin-top:8px;margin-bottom:4px;font-weight:600;">
          Avaliação de Desempenho:
        </div>
        <ul style="padding-left:18px;margin-top:0;">
          ${itens}
        </ul>
      `

      const observacoesInstrutor = this.observacoesInstrutorHtml

      return `
        ${this.preview.cabecalho_html || ''}
        ${lista}
        ${observacoesInstrutor}
        ${this.preview.rodape_html || ''}
      `
    },

    observacoesInstrutorHtml () {
      const observacoes = String(this.preview?.observacoes_instrutor || '').trim()

      if (!observacoes) return ''

      return `
        <div style="margin-top:14px;margin-bottom:4px;font-weight:600;">
          Observações do Instrutor:
        </div>
        <div style="white-space:pre-line;margin-bottom:12px;">
          ${this.escapeHtml(observacoes)}
        </div>
      `
    }
  },

  watch: {
    isVisible (v) {
      if (v && this.respostaId != null) {
        this.loadPreview()
      }
    },

    respostaId () {
      if (this.isVisible && this.respostaId != null) {
        this.loadPreview()
      }
    }
  },

  methods: {
    close () {
      if (this.isSending) return

      this.$emit('close')
      this.preview = null
      this.erro = null
    },

    fmtDate (iso) {
      return globalUtils?.formatarData
        ? globalUtils.formatarData(iso)
        : (iso || '')
    },

    escapeHtml (value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },

    formatResposta (value) {
      if (value === null || value === undefined) return ''

      if (Array.isArray(value)) {
        return value.join(', ')
      }

      const texto = String(value)

      // Se ainda vier alguma string JSON, tenta limpar para exibição.
      // Ex.: "\"Excelente\"" -> Excelente
      // Ex.: "[\"Bom\",\"Muito Bom\"]" -> Bom, Muito Bom
      try {
        const parsed = JSON.parse(texto)

        if (Array.isArray(parsed)) {
          return parsed.join(', ')
        }

        if (typeof parsed === 'string') {
          return parsed
        }

        if (typeof parsed === 'boolean') {
          return parsed ? 'Sim' : 'Não'
        }

        if (parsed !== null && parsed !== undefined) {
          return String(parsed)
        }
      } catch (_) {
        // mantém o texto original se não for JSON válido
      }

      return texto
    },

    async loadPreview () {
      if (this.respostaId == null) return

      this.loading = true
      this.erro = null
      this.preview = null

      try {
        const id = Number(this.respostaId)

        if (!Number.isFinite(id)) {
          throw new Error('respostaId inválido')
        }

        const { data } = await axios.get(`/dashboard/pendentes-envio/${id}/preview`)
        this.preview = data
      } catch (e) {
        console.error('Preview erro:', e)
        this.erro = 'Falha ao carregar pré-visualização.'
      } finally {
        this.loading = false
      }
    },

    formatReasons (reasons = {}, ids = []) {
      if (!Array.isArray(ids) || !ids.length) return ''

      return ids
        .map(id => `• #${id}: ${reasons?.[id] || 'sem motivo informado'}`)
        .join('\n')
    },

    async enviar () {
      if (!this.preview || !this.emailTo || this.respostaId == null) return

      this.isSending = true

      try {
        const id = Number(this.respostaId)

        if (!Number.isFinite(id)) {
          throw new Error('respostaId inválido')
        }

        const { data } = await axios.post(
          '/operacional/emails/enviar-avaliacao',
          {
            respostaIds: [id],
            permitirReenvio:
              this.modoReenvio === true
          }
        );

        if (Array.isArray(data.ok) && data.ok.includes(id)) {
          this.$emit('enviado', id)
          this.isSending = false
          this.close()
          return
        }

        const failMsg = Array.isArray(data.fail) && data.fail.includes(id)
          ? this.formatReasons(data.reasons, [id])
          : ''

        const skippedMsg = Array.isArray(data.skipped) && data.skipped.includes(id)
          ? this.formatReasons(data.reasons, [id])
          : ''

        const msg = failMsg || skippedMsg || 'Não foi possível enviar agora.'
        alert(msg)
      } catch (e) {
        console.error('Enviar e-mail erro:', e)
        alert('Falha ao enviar e-mail.')
      } finally {
        if (this.isSending) {
          this.isSending = false
        }
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 50%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  background: #ccc;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 0 0 auto;
}

.modal-footer {
  padding: 10px 15px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 0 auto;
}

.modal-content {
  padding: 15px;
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}

.btn {
  min-width: 70px;
  padding: 6px 15px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}

.email-preview {
  background: #fff;
}
</style>